import { useCallback, useMemo } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography/Text';

import { AppExperiments, useIsTreatment } from '../../../../utils/experiments';
import type { MemberRole, MemberRoleMap } from '../../utils/constants';
import {
  getHighestSelectedRole,
  getLockedRoleValues,
  getRolesImpliedBy,
} from '../../utils/roleHierarchy';

import type { ProductIconName } from './ProductIcon';
import { ProductIcon } from './ProductIcon';
import { ProductRolePopover } from './ProductRolePopover';

const messages = defineMessages({
  multipleRolesSummary: {
    id: 'Team.inviteTray.multipleRolesSummary',
    defaultMessage: '{count} roles',
    description: 'Summary label shown on a product row when multiple roles are selected',
  },
  noAccessSummary: {
    id: 'Team.inviteTray.noAccessSummary',
    defaultMessage: 'No access',
    description: 'Summary label shown on a product row when no roles are selected',
  },
});

const NO_LOCKED_ROLES: ReadonlySet<MemberRole> = new Set();

export type ProductAccessRowConfig = {
  productKey: string;
  iconName: ProductIconName;
  iconBackground: string;
  title: string;
  description: string;
  onCheckedChange?: (checked: boolean) => void;
  /**
   * All roles for the product. Unassignable held roles (`assignable === false`)
   * render checked + disabled so an admin-only member reads as having access.
   */
  roleOptions: MemberRoleMap[];
  selectedRoleValues: MemberRole[];
  onRoleToggle: (value: MemberRole, isSelected: boolean) => void;
  isEditable?: boolean;
  /** Hides the product icon for this row (IDFE-3663). */
  hideIcon?: boolean;
  /** Shown after the role picker, e.g. a `PendingRoleInfo` icon. */
  roleInfo?: ReactNode;
};

export function ProductAccessRow({
  productKey,
  iconName,
  iconBackground,
  title,
  description,
  onCheckedChange,
  roleOptions,
  selectedRoleValues,
  onRoleToggle,
  isEditable = true,
  hideIcon = false,
  roleInfo,
}: ProductAccessRowConfig) {
  const { formatMessage } = useIntl();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.target.checked);
    },
    [onCheckedChange],
  );

  // The roles the member holds for this product. `assignable` only governs
  // editability: unassignable held roles are seeded into the selection upstream,
  // so they show here as checked but locked.
  const checkedRoles = useMemo(
    () => roleOptions.filter((role) => selectedRoleValues.includes(role.value)),
    [roleOptions, selectedRoleValues],
  );
  const hasUnassignableRole = checkedRoles.some((role) => role.assignable === false);
  const checked = checkedRoles.length > 0;

  // IDFE-4045 (t0018_4fba688a): roles are hierarchical. Checking a role also
  // checks every role ranked below it, and those stay locked while it's checked.
  const isHierarchicalRoles = useIsTreatment(AppExperiments.t0018_4fba688a);

  // A held role's implied roles may be shown checked without being selected
  // (e.g. a member seeded with only Admin). Select them before unchecking the
  // higher role so they stay checked instead of vanishing with it.
  const handleRoleToggle = useCallback(
    (value: MemberRole, isSelected: boolean) => {
      if (!isHierarchicalRoles) {
        onRoleToggle(value, isSelected);
        return;
      }
      if (isSelected) onRoleToggle(value, true);
      for (const impliedRole of getRolesImpliedBy(roleOptions, value)) {
        if (!selectedRoleValues.includes(impliedRole)) {
          onRoleToggle(impliedRole, true);
        }
      }
      if (!isSelected) onRoleToggle(value, false);
    },
    [isHierarchicalRoles, onRoleToggle, roleOptions, selectedRoleValues],
  );

  const lockedRoleValues = useMemo(
    () =>
      isHierarchicalRoles ? getLockedRoleValues(roleOptions, selectedRoleValues) : NO_LOCKED_ROLES,
    [isHierarchicalRoles, roleOptions, selectedRoleValues],
  );

  // Under the hierarchy the highest role describes the selection, e.g. "Admin"
  // rather than "2 roles". Held unassignable roles keep the count summary.
  const highestSelectedRole = useMemo(
    () =>
      isHierarchicalRoles && !hasUnassignableRole
        ? getHighestSelectedRole(roleOptions, selectedRoleValues)
        : undefined,
    [hasUnassignableRole, isHierarchicalRoles, roleOptions, selectedRoleValues],
  );

  const roleLabel = useMemo(() => {
    if (highestSelectedRole != null) {
      return highestSelectedRole.name;
    }
    if (checkedRoles.length > 1) {
      return formatMessage(messages.multipleRolesSummary, { count: checkedRoles.length });
    }
    if (checkedRoles.length === 0) {
      return formatMessage(messages.noAccessSummary);
    }
    return checkedRoles[0].name;
  }, [formatMessage, checkedRoles, highestSelectedRole]);

  return (
    <HStack width="100%" alignItems="center" gap={2}>
      <HStack flexGrow={1} alignItems="center" gap={2}>
        <Checkbox
          testID={`team-product-access-checkbox-${productKey}`}
          value={productKey}
          accessibilityLabel={title}
          checked={checked}
          onChange={handleChange}
          // Lock the checkbox when the member holds an unassignable role — it
          // can't be removed from this tray.
          disabled={!isEditable || hasUnassignableRole}
          controlSize={22}
          borderRadius={0}
          background={checked ? 'fg' : undefined}
          borderColor={checked ? 'fg' : undefined}
        />
        <HStack flexGrow={1} alignItems="center" gap={1.5}>
          {!hideIcon && (
            <ProductIcon
              name={iconName}
              background={iconBackground}
              testID={`team-product-icon-${productKey}`}
            />
          )}
          <VStack gap={0} alignItems="flex-start" flexGrow={1}>
            <Text as="span" font="label1" color="fg" noWrap>
              {title}
            </Text>
            <Text as="span" font="label2" color="fgMuted" noWrap>
              {description}
            </Text>
          </VStack>
        </HStack>
      </HStack>
      <ProductRolePopover
        roleOptions={roleOptions}
        selectedRoleValues={selectedRoleValues}
        onRoleToggle={handleRoleToggle}
        lockedRoleValues={lockedRoleValues}
        roleLabel={roleLabel}
        productLabel={title}
        disabled={roleOptions.length === 0 || !isEditable}
      />
      {roleInfo}
    </HStack>
  );
}
